import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { KeyRound, MapPin, CalendarDays, Users, BookOpen, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const fmt = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

const MD = {
  h1: (p) => <h1 className="mt-6 mb-3 text-2xl font-semibold text-ink" {...p} />,
  h2: (p) => <h2 className="mt-6 mb-2 text-xl font-semibold text-ink" {...p} />,
  h3: (p) => <h3 className="mt-4 mb-2 text-lg font-semibold text-ink" {...p} />,
  p: (p) => <p className="mb-3 leading-relaxed text-ink/80" {...p} />,
  ul: (p) => <ul className="mb-3 list-disc space-y-1 pl-5 text-ink/80" {...p} />,
  ol: (p) => <ol className="mb-3 list-decimal space-y-1 pl-5 text-ink/80" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  a: (p) => <a className="font-medium text-camel underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
  strong: (p) => <strong className="font-semibold text-ink" {...p} />,
  blockquote: (p) => <blockquote className="my-3 border-l-2 border-camel pl-4 italic text-taupe" {...p} />,
};

export default function GuestPortal() {
  const [lastName, setLastName] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!lastName.trim() || !checkIn) return toast.error("Please enter your last name and check-in date");
    setLoading(true);
    try {
      const res = await api.post("/guest/lookup", { last_name: lastName, check_in: checkIn, email, opt_in: optIn });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "We couldn't find your stay. Please try again.");
    } finally { setLoading(false); }
  };

  if (data) {
    const first = (data.guest_name || "").split(" ")[0] || "there";
    return (
      <div className="min-h-screen bg-sand">
        <div className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
          <span className="overline text-camel">Guest Portal</span>
          <h1 data-testid="guest-welcome-heading" className="mt-2 text-4xl font-medium text-ink">Welcome, {first} 👋</h1>
          <p className="mt-2 text-taupe">Everything you need for a flawless stay is right here.</p>

          <div className="mt-8 space-y-4" data-testid="guest-trips">
            {data.trips.map((t, i) => (
              <div key={`${t.property_id}-${t.check_in}-${i}`} className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-2 text-camel"><MapPin size={16} /> <span className="text-sm font-medium">Your reservation</span></div>
                <h2 className="mt-2 text-2xl text-ink">{t.property_name}</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <div className="flex items-center gap-2 text-sm text-ink/80"><CalendarDays size={16} className="text-taupe" /> {fmt(t.check_in)} → {fmt(t.check_out)}</div>
                  <div className="flex items-center gap-2 text-sm text-ink/80"><KeyRound size={16} className="text-taupe" /> {t.nights} night{t.nights === 1 ? "" : "s"}</div>
                  <div className="flex items-center gap-2 text-sm text-ink/80"><Users size={16} className="text-taupe" /> {t.guests} guest{t.guests === 1 ? "" : "s"}</div>
                </div>
                {t.channel && <p className="mt-4 text-xs text-taupe">Booked via {t.channel}</p>}
              </div>
            ))}
          </div>

          {data.trips.filter((t) => t.guide_content || t.guide_url).map((t, i) => (
            <div key={`guide-${t.property_id}-${i}`} className="mt-8 rounded-3xl border border-black/5 bg-white p-7 shadow-sm" data-testid="guest-guide">
              <div className="flex items-center gap-2 text-camel"><BookOpen size={18} /> <span className="overline">Your Welcome Guide · {t.property_name}</span></div>
              {t.guide_url && (
                <a href={t.guide_url} target="_blank" rel="noreferrer" data-testid="guide-open-link" className="mt-3 inline-block">
                  <Button className="rounded-full btn-ink">Open full guide <ExternalLink size={15} className="ml-1.5" /></Button>
                </a>
              )}
              {t.guide_content && (
                <div className="mt-4 text-[15px]">
                  <ReactMarkdown components={MD}>{t.guide_content}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}

          {!data.trips.some((t) => t.guide_content || t.guide_url) && (
            <div className="mt-8 rounded-3xl border border-dashed border-black/10 bg-white p-7 text-center text-taupe" data-testid="guest-guide-empty">
              Your welcome guide will appear here soon. Your host will also email you check-in details before arrival.
            </div>
          )}

          <button data-testid="guest-signout" onClick={() => { setData(null); setEmail(""); setLastName(""); }}
            className="mt-8 text-sm font-medium text-taupe hover:text-ink">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand px-5 py-14">
      <form onSubmit={submit} data-testid="guest-login-form" className="w-full max-w-md rounded-3xl border border-black/5 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ink text-white"><KeyRound size={20} /></span>
          <h1 className="mt-4 text-2xl text-ink">Access your stay</h1>
          <p className="mt-1 text-sm text-taupe">Enter the last name and check-in date from your booking to view your welcome guide.</p>
        </div>
        <label className="mb-1 block text-xs font-medium text-taupe">Last name</label>
        <Input data-testid="guest-lastname" placeholder="e.g. Ramos" value={lastName}
          onChange={(e) => setLastName(e.target.value)} className="mb-3 rounded-2xl border-black/10" />
        <label className="mb-1 block text-xs font-medium text-taupe">Check-in date</label>
        <Input data-testid="guest-checkin" type="date" value={checkIn}
          onChange={(e) => setCheckIn(e.target.value)} className="mb-3 rounded-2xl border-black/10" />
        <label className="mb-1 block text-xs font-medium text-taupe">Email <span className="text-taupe/60">(optional)</span></label>
        <Input data-testid="guest-email" type="email" placeholder="you@example.com" value={email}
          onChange={(e) => setEmail(e.target.value)} className="mb-3 rounded-2xl border-black/10" />
        <label className="mb-5 flex items-start gap-2 text-sm text-ink/80">
          <Checkbox data-testid="guest-optin" checked={optIn} onCheckedChange={(v) => setOptIn(!!v)} className="mt-0.5" />
          <span>Email me local tips, guides &amp; exclusive direct-booking offers.</span>
        </label>
        <Button data-testid="guest-login-btn" type="submit" disabled={loading} className="h-12 w-full rounded-full btn-ink">
          {loading ? "Finding your stay…" : "View my welcome guide"}
        </Button>
      </form>
    </div>
  );
}
